import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import {
  Clock,
  Calendar,
  Wallet,
  ChevronRight,
  MoreHorizontal,
  TrendingUp,
  CheckCircle2,
  CheckCircle,
  X,
  AlertCircle,
  MapPin,
  FileText,
  Package,
  Laptop,
  Wrench,
  RefreshCw,
} from 'lucide-react';
import { isPendingIzin } from '../utils/izinApproval';
import { isPendingUang } from '../utils/uangApproval';


const hasApprovalValue = (val) => {
  if (val === null || val === undefined || val === false) return false;
  if (val === 0 || val === '0') return false;
  return String(val).trim() !== '';
};

const getIzinApproverInfo = (item, defaultDept = '', defaultLevel = '') => {
  if (item.acc === 2 || item.telat === 2) {
    return { label: 'Ditolak', initial: '✕', color: '#ef4444' };
  }

  const level = (item.level || defaultLevel || '').toLowerCase().trim();
  const dept = (item.departement || item.department || defaultDept || '').toLowerCase().trim();
  const karyId = String(item.karyawan_id || '');
  const isSpecialId = karyId === '72' || karyId === '475';
  const isKoordinator = level.includes('koordinator') || level.includes('leader');
  const isOps = dept === 'operasional';

  // Jika sudah disetujui (selesai)
  if (hasApprovalValue(item.ttd_kacab)) {
    const finalApprover = (!isKoordinator && isOps && !isSpecialId) ? 'Supervisor' : 'Kacab/HC';
    return { label: `Oleh ${finalApprover}`, initial: finalApprover[0], color: '#10b981' };
  }

  // Menunggu Pengganti
  const hasPengganti = Boolean(item.nama_pengganti && item.nama_pengganti !== '-' && item.nama_pengganti.trim() !== '');
  if (hasPengganti && !hasApprovalValue(item.ttd_pengganti)) {
    const firstName = item.nama_pengganti.trim().split(' ')[0];
    return { label: `Menunggu: ${firstName}`, initial: 'P', color: '#f59e0b' };
  }

  if (isSpecialId || (isKoordinator && !isOps)) {
    if (!hasApprovalValue(item.ttd_kacab)) {
      return { label: 'Menunggu: Kacab/HC', initial: 'K', color: '#3b82f6' };
    }
  } else if (isKoordinator && isOps) {
    if (!hasApprovalValue(item.ttd_koordinator)) {
      return { label: 'Menunggu: SPV', initial: 'S', color: '#f59e0b' };
    }
    if (!hasApprovalValue(item.ttd_kacab)) {
      return { label: 'Menunggu: Kacab/HC', initial: 'K', color: '#3b82f6' };
    }
  } else {
    // Staff
    if (!hasApprovalValue(item.ttd_koordinator)) {
      return { label: 'Menunggu: Koordinator', initial: 'K', color: '#f59e0b' };
    }
    if (!hasApprovalValue(item.ttd_kacab)) {
      return {
        label: isOps ? 'Menunggu: SPV' : 'Menunggu: Kacab/HC',
        initial: isOps ? 'S' : 'K',
        color: '#3b82f6'
      };
    }
  }

  return { label: 'Oleh Kacab/HC', initial: 'K', color: '#10b981' };
};

const getDetailUangType = (item = {}) => {
  const absenId = String(item.absen_id || '').toLowerCase();
  const potId = Number(item.potongan_id);
  const text = String(
    item.keperluan ||
    item.keterangan ||
    item.alasan ||
    item.nama ||
    item.jenis ||
    ''
  ).toLowerCase();

  // 1. Cek absen_id spesifik atau potongan_id
  if (absenId.includes('kasbon') || potId === 3) return 'Kasbon';
  if (absenId.includes('lembur') || potId === 22) return 'Lembur';
  if (absenId.includes('piket') || absenId.includes('perconnote') || potId === 11) return 'Piket';
  if (absenId.includes('cicil') || (potId && potId !== 3 && potId !== 22 && potId !== 11)) return 'Cicilan';

  // 2. Cek teks keperluan/keterangan/alasan/nama
  if (text.includes('kasbon')) return 'Kasbon';
  if (text.includes('lembur')) return 'Lembur';
  if (text.includes('piket') || text.includes('perconnote')) return 'Piket';
  if (text.includes('cicil')) return 'Cicilan';

  return 'Uang';
};

const getUangApproverInfo = (item) => {
  if (item.acc === 2) {
    return { label: 'Ditolak', initial: '✕', color: '#ef4444' };
  }

  const uType = getDetailUangType(item);
  const isKasbonCicilan = uType === 'Kasbon' || uType === 'Cicilan';

  if (isKasbonCicilan) {
    if (hasApprovalValue(item.ttd_direksi)) {
      return { label: 'Oleh Direksi', initial: 'D', color: '#10b981' };
    }
    if (!hasApprovalValue(item.ttd_hc)) {
      return { label: 'Menunggu: HC', initial: 'H', color: '#f59e0b' };
    }
    return { label: 'Menunggu: Direksi', initial: 'D', color: '#3b82f6' };
  } else {
    if (hasApprovalValue(item.ttd_hc)) {
      return { label: 'Oleh HC', initial: 'H', color: '#10b981' };
    }
    if (item.ttd_direksi !== 'T' && !hasApprovalValue(item.ttd_direksi)) {
      return { label: 'Menunggu: SPV', initial: 'S', color: '#f59e0b' };
    }
    return { label: 'Menunggu: HC', initial: 'H', color: '#3b82f6' };
  }
};

const getAbsenApproverInfo = (item, type = 'lapangan') => {
  if (type === 'lupa') {
    if (item.acc === 1 || item.status === 'Disetujui') {
      return { label: 'Oleh HRD', initial: 'H', color: '#10b981' };
    }
    if (item.acc === 2 || item.status === 'Ditolak') {
      return { label: 'Ditolak', initial: '✕', color: '#ef4444' };
    }
    return { label: 'Menunggu: HRD', initial: 'H', color: '#f59e0b' };
  }

  if (item.acc === 1) {
    return { label: 'Oleh Atasan', initial: 'A', color: '#10b981' };
  }
  if (item.acc === 2) {
    return { label: 'Ditolak', initial: '✕', color: '#ef4444' };
  }
  return { label: 'Menunggu: Atasan', initial: 'A', color: '#f59e0b' };
};

const checkIsAgen = () => {
  try {
    const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const u = JSON.parse(localStorage.getItem('data_user') || '{}');
    return k.is_agen === true || u.is_agen === true || u.role === 'agen' || localStorage.getItem('is_agen') === 'true';
  } catch {
    return false;
  }
};

const RiwayatNotifikasiCard = ({ navigate }) => {
  const isAgen = checkIsAgen();
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'absen', 'izin', 'uang', 'atk', 'it'
  const [trigger, setTrigger] = useState(0);

  // ── State untuk ATK & IT (karyawan non-agen) ──
  const [karyawanAtkItItems, setKaryawanAtkItItems] = useState([]);
  const [karyawanAtkItLoading, setKaryawanAtkItLoading] = useState(false);
  const [selectedAtkOrderKaryawan, setSelectedAtkOrderKaryawan] = useState(null);

  useEffect(() => {
    const handleUpdate = () => setTrigger(t => t + 1);
    window.addEventListener('storage', handleUpdate);
    window.addEventListener('badgeUpdate', handleUpdate);

    return () => {
      window.removeEventListener('storage', handleUpdate);
      window.removeEventListener('badgeUpdate', handleUpdate);
    };
  }, []);

  // Format Helper Tanggal
  const formatTanggal = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Kumpulkan semua notifikasi dari data /lapor
  const { allItems, countAbsen, countIzin, countUang } = useMemo(() => {
    const items = [];

    // ==========================================
    // 1. DATA ABSEN (laporanku, teamku, lupa absen)
    // ==========================================
    try {
      const serverAbsen = JSON.parse(localStorage.getItem('absen_server') || '[]');
      const konfirmasiAbsen = JSON.parse(localStorage.getItem('absen_konfirmasi') || '[]');
      const lupaAbsen = JSON.parse(localStorage.getItem('lapor_lupa_absen') || '[]');
      const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const namaUser = karyawan?.nama_lengkap || karyawan?.nama || 'Saya';

      // A. Laporanku Lapangan / GPS Error
      const filterLaporanku = (item) =>
        item.absen_id === 18 &&
        (item.log === 'lapangan' || item.log === 'gps error') &&
        item.nama !== null;

      serverAbsen.filter(filterLaporanku).forEach((item, idx) => {
        const isMasuk = item.status?.includes('Masuk') || item.jam_masuk;
        const tipeLabel = isMasuk ? 'Absen Masuk Lapangan' : item.status ? 'Absen Pulang Lapangan' : item.log === 'lapangan' ? 'Absen Lapangan' : 'Absen GPS Error';
        const isApproved = item.acc === 1;
        const isRejected = item.acc === 2;
        const isPerluVerif = item.status_validasi === 'perlu_verifikasi';

        const approverInfo = getAbsenApproverInfo(item, 'lapangan');
        items.push({
          id: `absen-mine-${item.id || item.tanggal + idx}`,
          category: 'absen',
          categoryLabel: 'Absen',
          tabKey: 'absen',
          title: tipeLabel,
          subtitle: item.catatan_validasi || item.lokasi || (item.waktu ? `Jam: ${item.waktu}` : 'Menunggu verifikasi atasan'),
          date: formatTanggal(item.tanggal),
          rawDate: new Date(item.tanggal || Date.now()).getTime(),
          statusText: isApproved ? 'Disetujui' : isRejected ? 'Ditolak' : isPerluVerif ? 'Perlu Verifikasi' : 'Menunggu',
          statusType: isApproved ? 'approved' : isRejected ? 'rejected' : 'ongoing',
          progress: isApproved ? 100 : isRejected ? 100 : 55,
          createdBy: namaUser,
          authorName: 'Saya',
          isMine: true,
          approverLabel: approverInfo.label,
          approverInitial: approverInfo.initial,
          approverColor: approverInfo.color,
          iconType: 'map',
        });
      });

      // B. Laporan Teamku (jika atasan)
      konfirmasiAbsen.filter(item => item.absen_id === 18 && (item.log === 'lapangan' || item.log === 'gps error')).forEach((item, idx) => {
        const sender = item.nama_karyawan || item.nama || 'Anggota Tim';
        items.push({
          id: `absen-team-${item.id || item.tanggal + idx}`,
          category: 'absen',
          categoryLabel: 'Absen Tim',
          tabKey: 'absen',
          title: `Laporan Absen: ${sender}`,
          subtitle: `${item.log === 'lapangan' ? 'Absen Lapangan' : 'GPS Error'} • Menunggu approval Anda`,
          date: formatTanggal(item.tanggal),
          rawDate: new Date(item.tanggal || Date.now()).getTime(),
          statusText: 'Perlu Approval',
          statusType: 'pending',
          progress: 35,
          createdBy: sender,
          authorName: sender,
          isMine: false,
          approverLabel: `Dari ${sender}`,
          approverInitial: (sender || 'U')[0].toUpperCase(),
          approverColor: '#2563eb',
          iconType: 'team',
        });
      });

      // C. Lupa Absen
      if (Array.isArray(lupaAbsen)) {
        lupaAbsen.forEach((item, idx) => {
          const sender = item.nama_karyawan || item.nama || namaUser;
          const isMine = sender === namaUser || sender === 'Saya';
          const approverInfo = getAbsenApproverInfo(item, 'lupa');
          items.push({
            id: `absen-lupa-${item.id || idx}`,
            category: 'absen',
            categoryLabel: 'Lupa Absen',
            tabKey: 'absen',
            title: `Lupa Absen: ${item.keterangan || item.alasan || 'Pengajuan Lupa Absen'}`,
            subtitle: `${item.tanggal || ''} ${item.jam ? `• Jam: ${item.jam}` : ''}`,
            date: formatTanggal(item.tanggal),
            rawDate: new Date(item.tanggal || Date.now()).getTime(),
            statusText: approverInfo.label === 'Disetujui' ? 'Disetujui' : approverInfo.label === 'Ditolak' ? 'Ditolak' : 'Menunggu HRD',
            statusType: approverInfo.label === 'Disetujui' ? 'approved' : approverInfo.label === 'Ditolak' ? 'rejected' : 'ongoing',
            progress: approverInfo.label === 'Disetujui' ? 100 : 45,
            createdBy: sender,
            authorName: isMine ? 'Saya' : sender,
            isMine: isMine,
            approverLabel: isMine ? approverInfo.label : `Dari ${sender}`,
            approverInitial: isMine ? approverInfo.initial : (sender || 'U')[0].toUpperCase(),
            approverColor: isMine ? approverInfo.color : '#2563eb',
            iconType: 'clock',
          });
        });
      }
    } catch (e) {
      console.error('Error membaca data absen untuk riwayat notif:', e);
    }

    // ==========================================
    // 2. DATA IZIN (ijin_pengajuan, ijin_konfirmasi)
    // ==========================================
    try {
      const ijinPengajuan = JSON.parse(localStorage.getItem('ijin_pengajuan') || '[]');
      const ijinKonfirmasi = JSON.parse(localStorage.getItem('ijin_konfirmasi') || '[]');
      const jenisIzinList = JSON.parse(localStorage.getItem('jenis_izin_list') || '[]');
      const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const namaUser = karyawan?.nama_lengkap || karyawan?.nama || 'Saya';

      const idsKeuangan = ['kasbon', 'cicilan', 'lembur', 'piket_harian', 'perconnote', 'piket_perconnote'];

      // A. Pengajuan Izin Saya (non keuangan)
      const myIzin = Array.isArray(ijinPengajuan)
        ? ijinPengajuan.filter(it => !idsKeuangan.includes(it.absen_id) && !it.potongan_id)
        : [];

      myIzin.forEach((item, idx) => {
        const foundJenis = jenisIzinList.find(j => String(j.id) === String(item.absen_id));
        const jenisNama = foundJenis?.nama || item.nama || 'Pengajuan Izin';
        const isPending = isPendingIzin(item);
        const isRejected = item.acc === 2 || item.telat === 2;

        let progress = 35;
        if (!isPending) progress = 100;
        else if (item.ttd_atasan) progress = 70;

        const userDept = karyawan?.departement || karyawan?.department || '';
        const userLevel = karyawan?.level || '';
        const approverInfo = getIzinApproverInfo(item, userDept, userLevel);
        const isFullyApproved = !isPendingIzin(item);
        const derivedStatusText = isRejected
          ? 'Ditolak'
          : isFullyApproved
            ? 'Disetujui'
            : approverInfo.label.startsWith('Menunggu')
              ? approverInfo.label
              : 'Menunggu';
        const derivedStatusType = isRejected ? 'rejected' : isFullyApproved ? 'approved' : 'ongoing';

        items.push({
          id: `izin-mine-${item.id || idx}`,
          category: 'izin',
          categoryLabel: 'Izin',
          tabKey: 'ijin',
          title: jenisNama,
          subtitle: item.keterangan || item.alasan || (item.dari ? `Periode: ${item.dari} s/d ${item.sampai || item.dari}` : 'Menunggu persetujuan'),
          date: formatTanggal(item.dari || item.tanggal || item.created_at),
          rawDate: new Date(item.dari || item.tanggal || item.created_at || Date.now()).getTime(),
          statusText: derivedStatusText,
          statusType: derivedStatusType,
          progress: progress,
          createdBy: namaUser,
          authorName: 'Saya',
          isMine: true,
          approverLabel: approverInfo.label,
          approverInitial: approverInfo.initial,
          approverColor: approverInfo.color,
          iconType: 'calendar',
        });
      });

      // B. Konfirmasi Izin Tim
      if (Array.isArray(ijinKonfirmasi)) {
        ijinKonfirmasi.filter(isPendingIzin).forEach((item, idx) => {
          const sender = item.nama_karyawan || item.nama || 'Anggota Tim';
          items.push({
            id: `izin-team-${item.id || idx}`,
            category: 'izin',
            categoryLabel: 'Izin Tim',
            tabKey: 'ijin',
            title: `Izin: ${sender}`,
            subtitle: `${item.alasan || item.keterangan || 'Pengajuan izin'} • Menunggu approval Anda`,
            date: formatTanggal(item.dari || item.tanggal),
            rawDate: new Date(item.dari || item.tanggal || Date.now()).getTime(),
            statusText: 'Perlu Approval',
            statusType: 'pending',
            progress: 40,
            createdBy: sender,
            authorName: sender,
            iconType: 'team',
          });
        });
      }
    } catch (e) {
      console.error('Error membaca data izin untuk riwayat notif:', e);
    }

    // ==========================================
    // 3. DATA UANG (uang_pengajuanKasbon, ijin_pengajuan keuangan, uang_konfirmasiPotongan)
    // ==========================================
    try {
      const kasbonList = JSON.parse(localStorage.getItem('uang_pengajuanKasbon') || '[]');
      const ijinList = JSON.parse(localStorage.getItem('ijin_pengajuan') || '[]');
      const konfirmasiPotongan = JSON.parse(localStorage.getItem('uang_konfirmasiPotongan') || '[]');
      const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const namaUser = karyawan?.nama_lengkap || karyawan?.nama || 'Saya';

      const idsKeuangan = ['kasbon', 'cicilan', 'lembur', 'piket_harian', 'piket_perconnote', 'perconnote'];

      // A. Pengajuan Keuangan Saya
      const myUangItems = [
        ...(Array.isArray(kasbonList) ? kasbonList : []),
        ...(Array.isArray(ijinList) ? ijinList.filter(it => idsKeuangan.includes(it.absen_id) || it.potongan_id) : [])
      ];

      myUangItems.forEach((item, idx) => {
        const typeLabel = getDetailUangType(item);

        const nominalStr = item.nominal
          ? ` Rp ${Number(item.nominal).toLocaleString('id-ID')}`
          : '';

        const isPending = isPendingUang(item);
        const isRejected = item.acc === 2;

        let progress = 35;
        if (!isPending) progress = 100;
        else if (item.ttd_atasan) progress = 70;

        const approverInfoU = getUangApproverInfo(item);
        const isFullyApprovedU = !isPendingUang(item);
        const derivedStatusTextU = isRejected
          ? 'Ditolak'
          : isFullyApprovedU
            ? 'Disetujui'
            : approverInfoU.label.startsWith('Menunggu')
              ? approverInfoU.label
              : 'Menunggu';
        const derivedStatusTypeU = isRejected ? 'rejected' : isFullyApprovedU ? 'approved' : 'ongoing';

        items.push({
          id: `uang-mine-${item.id || idx}`,
          category: 'uang',
          categoryLabel: typeLabel,
          tabKey: 'kasbon',
          title: `Pengajuan ${typeLabel}${nominalStr}`,
          subtitle: item.keperluan || item.keterangan || item.alasan || `Pengajuan ${typeLabel.toLowerCase()} operasional`,
          date: formatTanggal(item.tanggal || item.created_at),
          rawDate: new Date(item.tanggal || item.created_at || Date.now()).getTime(),
          statusText: derivedStatusTextU,
          statusType: derivedStatusTypeU,
          progress: progress,
          createdBy: namaUser,
          authorName: 'Saya',
          isMine: true,
          approverLabel: approverInfoU.label,
          approverInitial: approverInfoU.initial,
          approverColor: approverInfoU.color,
          iconType: 'wallet',
        });
      });

      // B. Konfirmasi Uang Tim
      if (Array.isArray(konfirmasiPotongan)) {
        konfirmasiPotongan.filter(isPendingUang).forEach((item, idx) => {
          const sender = item.nama_karyawan || item.nama || 'Anggota Tim';
          const typeLabel = getDetailUangType(item);
          items.push({
            id: `uang-team-${item.id || idx}`,
            category: 'uang',
            categoryLabel: `${typeLabel} Tim`,
            tabKey: 'kasbon',
            title: `Konfirmasi ${typeLabel}: ${sender}`,
            subtitle: `${item.keperluan || `Menunggu verifikasi ${typeLabel.toLowerCase()}`}`,
            date: formatTanggal(item.tanggal),
            rawDate: new Date(item.tanggal || Date.now()).getTime(),
            statusText: 'Perlu Approval',
            statusType: 'pending',
            progress: 40,
            createdBy: sender,
            authorName: sender,
            iconType: 'team',
          });
        });
      }
    } catch (e) {
      console.error('Error membaca data uang untuk riwayat notif:', e);
    }

    // Urutkan berdasarkan tanggal terbaru
    items.sort((a, b) => b.rawDate - a.rawDate);

    // ── Filter Periode Berjalan untuk item yang sudah selesai (approved/rejected) ──
    // Periode: tanggal 21 bulan lalu s/d tanggal 20 bulan ini
    // Item pending/ongoing SELALU tampil; item approved/rejected hanya jika masih dalam periode
    const now = new Date();
    const currentDay = now.getDate();
    const currentMonth = now.getMonth(); // 0-indexed
    const currentYear = now.getFullYear();

    // Awal periode: tanggal 21 bulan lalu
    const periodeStart = new Date(
      currentDay >= 21
        ? currentYear
        : currentMonth === 0 ? currentYear - 1 : currentYear,
      currentDay >= 21
        ? currentMonth
        : currentMonth === 0 ? 11 : currentMonth - 1,
      21, 0, 0, 0
    );

    const filteredItems = items.filter(item => {
      if (item.statusType === 'pending' || item.statusType === 'ongoing') return true; // selalu tampil
      // approved / rejected: hanya tampil jika dalam periode berjalan
      return item.rawDate >= periodeStart.getTime();
    });

    const countAbsen = filteredItems.filter(i => i.category === 'absen').length;
    const countIzin = filteredItems.filter(i => i.category === 'izin').length;
    const countUang = filteredItems.filter(i => i.category === 'uang').length;

    return { allItems: filteredItems, countAbsen, countIzin, countUang };
  }, [trigger]);

  // Gabungkan allItems dengan karyawanAtkItItems (untuk non-agen)
  const combinedItems = useMemo(() => {
    if (isAgen) return allItems;
    const merged = [...allItems, ...karyawanAtkItItems];
    merged.sort((a, b) => b.rawDate - a.rawDate);
    return merged;
  }, [allItems, karyawanAtkItItems, isAgen]);

  const countAtk = useMemo(() => combinedItems.filter(i => i.category === 'atk').length, [combinedItems]);
  const countIt = useMemo(() => combinedItems.filter(i => i.category === 'it').length, [combinedItems]);
  const countAsset = useMemo(() => combinedItems.filter(i => i.category === 'asset').length, [combinedItems]);

  // Filter list
  const filteredList = useMemo(() => {
    if (activeFilter === 'all') return combinedItems;
    return combinedItems.filter(item => item.category === activeFilter);
  }, [combinedItems, activeFilter]);

  // Hanya tampilkan maksimal 4 kartu pertama di dashboard agar ringkas & rapi
  const displayedItems = filteredList.slice(0, 4);

  const handleCardClick = (item) => {
    if (item.category === 'atk') {
      if (item.rawOrder) {
        setSelectedAtkOrderKaryawan(item.rawOrder);
      } else {
        navigate('/atk-pemesanan');
      }
      return;
    }
    if (item.category === 'it') {
      navigate('/request-it');
      return;
    }
    if (item.category === 'asset') {
      navigate('/lapor', { state: { activeTab: 'asset' } });
      return;
    }
    navigate('/lapor', { state: { activeTab: item.tabKey } });
  };

  const handleViewAll = () => {
    const tabMap = {
      all: 'absen',
      absen: 'absen',
      izin: 'ijin',
      uang: 'kasbon',
      asset: 'asset',
      atk: 'atk',
      it: 'it',
    };
    if (activeFilter === 'atk') { navigate('/atk-pemesanan'); return; }
    if (activeFilter === 'it') { navigate('/request-it'); return; }
    if (activeFilter === 'asset') { navigate('/lapor', { state: { activeTab: 'asset' } }); return; }
    navigate('/lapor', { state: { activeTab: tabMap[activeFilter] || 'absen' } });
  };

  // Helper render ikon kategori
  const renderCategoryIcon = (category, iconType, isDark) => {
    if (category === 'atk') {
      return (
        <div style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: isDark ? 'rgba(124, 58, 237, 0.2)' : '#ffffff',
          border: isDark ? '1px solid rgba(124, 58, 237, 0.3)' : '1px solid #cbd5e1',
          boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isDark ? '#c084fc' : '#7c3aed',
          flexShrink: 0,
        }}>
          <Package size={20} />
        </div>
      );
    }
    if (category === 'it') {
      return (
        <div style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: isDark ? 'rgba(2, 132, 199, 0.2)' : '#ffffff',
          border: isDark ? '1px solid rgba(2, 132, 199, 0.3)' : '1px solid #cbd5e1',
          boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isDark ? '#38bdf8' : '#0284c7',
          flexShrink: 0,
        }}>
          <Laptop size={20} />
        </div>
      );
    }
    if (category === 'asset') {
      return (
        <div style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: isDark ? 'rgba(13, 148, 136, 0.2)' : '#ffffff',
          border: isDark ? '1px solid rgba(13, 148, 136, 0.3)' : '1px solid #cbd5e1',
          boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isDark ? '#2dd4bf' : '#0d9488',
          flexShrink: 0,
        }}>
          <Wrench size={20} />
        </div>
      );
    }
    if (category === 'absen') {
      return (
        <div style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: isDark ? 'rgba(59, 130, 246, 0.2)' : '#ffffff',
          border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid #cbd5e1',
          boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isDark ? '#60a5fa' : '#0284c7',
          flexShrink: 0,
        }}>
          {iconType === 'map' ? <MapPin size={20} /> : <Clock size={20} />}
        </div>
      );
    }
    if (category === 'izin') {
      return (
        <div style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: isDark ? 'rgba(168, 85, 247, 0.2)' : '#ffffff',
          border: isDark ? '1px solid rgba(168, 85, 247, 0.3)' : '1px solid #cbd5e1',
          boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isDark ? '#c084fc' : '#9333ea',
          flexShrink: 0,
        }}>
          <Calendar size={20} />
        </div>
      );
    }
    // Uang
    return (
      <div style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        background: isDark ? 'rgba(16, 185, 129, 0.2)' : '#ffffff',
        border: isDark ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #cbd5e1',
        boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: isDark ? '#34d399' : '#059669',
        flexShrink: 0,
      }}>
        <Wallet size={20} />
      </div>
    );
  };

  // ── AGEN: state untuk riwayat ATK / IT / Asset ──
  const [agenItems, setAgenItems] = useState([]);
  const [agenLoading, setAgenLoading] = useState(false);
  const [agenFilter, setAgenFilter] = useState('all'); // 'all' | 'atk' | 'it' | 'asset'
  const [selectedAtkOrder, setSelectedAtkOrder] = useState(null);

  // Helper: getUserId (shared)
  const getUserIdShared = () => {
    try { return JSON.parse(localStorage.getItem('data_karyawan') || '{}')?.id || null; }
    catch { return null; }
  };

  const isFemaleUserShared = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('data_user') || '{}');
      const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const g = (u.gender || u.jenis_kelamin || k.gender || k.jenis_kelamin || '').toString().trim().toLowerCase();
      return g === 'female' || g === 'perempuan' || g === 'wanita' || g === 'p';
    } catch { return false; }
  })();

  const getAgenNameShared = () => {
    try {
      const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const u = JSON.parse(localStorage.getItem('data_user') || '{}');
      return k.agen || k.nama_agen || u.agen || u.nama_agen || [k.nama_depan, k.nama_belakang].filter(Boolean).join(' ') || k.nama || u.nama || 'Karyawan';
    } catch { return 'Karyawan'; }
  };

  const isFemaleUser = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('data_user') || '{}');
      const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const g = (u.gender || u.jenis_kelamin || k.gender || k.jenis_kelamin || '').toString().trim().toLowerCase();
      return g === 'female' || g === 'perempuan' || g === 'wanita' || g === 'p';
    } catch { return false; }
  })();

  const getUserId = () => {
    try { return JSON.parse(localStorage.getItem('data_karyawan') || '{}')?.id || null; }
    catch { return null; }
  };

  const getAgenName = () => {
    try {
      const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const u = JSON.parse(localStorage.getItem('data_user') || '{}');
      return k.agen || k.nama_agen || u.agen || u.nama_agen || [k.nama_depan, k.nama_belakang].filter(Boolean).join(' ') || k.nama || u.nama || 'Agen';
    } catch { return 'Agen'; }
  };

  // ── Fetch ATK & IT untuk karyawan (non-agen) ──
  useEffect(() => {
    if (isAgen) return; // agen punya fetch sendiri di bawah
    let cancelled = false;

    const loadKaryawanAtkIt = async () => {
      setKaryawanAtkItLoading(true);
      const userId = getUserIdShared();
      const result = [];

      // 1. ATK
      if (!isFemaleUserShared) {
        try {
          const getAtkStatusInfoK = (rawStatus, petugas, agenNameParam) => {
            const s = String(rawStatus || 'baru').toLowerCase().trim();
            const approverName = petugas ? `Oleh ${petugas}` : 'Oleh GA/HC';
            const approverInitial = petugas
              ? petugas.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
              : 'GA';
            if (['diterima', 'terima', 'erima', 'sudah diterima'].some(k => s.includes(k))) {
              const rawAgen = agenNameParam || getAgenNameShared() || 'Karyawan';
              const cleanAgen = String(rawAgen).replace(/^oleh\s+/i, '').trim();
              const agenInitial = cleanAgen.split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'KR';
              return { label: 'Sudah Diterima', type: 'approved', progress: 100, approver: `Oleh ${cleanAgen}`, initial: agenInitial, color: '#0891b2' };
            }
            if (['dikirim', 'kirim', 'sudah dikirim'].some(k => s.includes(k)))
              return { label: 'Sudah Dikirim', type: 'approved', progress: 90, approver: approverName, initial: approverInitial, color: '#7c3aed' };
            if (['diisiapkan', 'disiapkan', 'approved', 'selesai', 'siap', 'sudah disiapkan'].some(k => s.includes(k)) || s.includes('siap'))
              return { label: 'Sudah Disiapkan', type: 'approved', progress: 75, approver: approverName, initial: approverInitial, color: '#059669' };
            if (['proses', 'diproses', 'sedang disiapkan'].some(k => s.includes(k)))
              return { label: 'Sedang Disiapkan', type: 'process', progress: 50, approver: approverName, initial: approverInitial, color: '#2563eb' };
            if (['rejected', 'ditolak', 'batal'].some(k => s.includes(k)))
              return { label: 'Ditolak', type: 'rejected', progress: 100, approver: approverName, initial: '✕', color: '#dc2626' };
            return { label: 'Ngantri / Belum Disiapkan', type: 'pending', progress: 20, approver: 'Menunggu Disiapkan', initial: 'GA', color: '#d97706' };
          };

          const pushATK = (o, idx) => {
            const st = getAtkStatusInfoK(o.status, o.petugas, o.agen || o.pemohon);
            const itemNames = Array.isArray(o.items) && o.items.length > 0
              ? o.items.map(i => i.nama + (i.qty ? ` (${i.qty})` : '')).join(', ')
              : (o.keperluan || 'Kebutuhan ATK Operasional');
            result.push({
              id: `katk-${o.id || o.no_order || idx}`,
              category: 'atk',
              categoryLabel: 'ATK',
              title: o.no_order || `Order ATK #${o.id || idx + 1}`,
              subtitle: itemNames,
              date: formatTanggal(o.tanggal || o.created_at),
              rawDate: new Date(o.tanggal || o.created_at || Date.now()).getTime(),
              statusText: st.label,
              statusType: st.type,
              progress: st.progress,
              approverLabel: st.approver,
              approverInitial: st.initial,
              approverColor: st.color,
              route: '/atk-pemesanan',
              rawOrder: o,
            });
          };

          let atkLoaded = false;
          if (userId) {
            try {
              const atkRes = await fetch(`https://jne-cilacap.com/slip/public/api/atk/orders?user_created=${userId}`);
              if (atkRes.ok) {
                const atkJson = await atkRes.json();
                if (atkJson.status === 'success' && Array.isArray(atkJson.data) && atkJson.data.length > 0) {
                  const mapped = atkJson.data.map((o) => ({
                    id: o.id,
                    raw_id: o.id,
                    no_order: o.no_nota || `ATK-${o.id}`,
                    items: (o.details || []).map(d => ({
                      id: d.id_barang,
                      nama: d.barang?.nama || d.nama_barang || 'Barang',
                      qty: d.jumlah,
                      satuan: d.barang?.satuan || 'Pcs',
                      kode: d.barang?.kode || '-',
                    })),
                    keperluan: o.keterangan || '-',
                    instruksi: o.keterangan || '-',
                    pemohon: o.agen || getAgenNameShared(),
                    lokasi: o.shift || '-',
                    status: o.status || 'baru',
                    petugas: o.petugas || null,
                    agen: o.agen || o.connote_agen || null,
                    catatan: o.catatan || null,
                    catatan_kirim: o.catatan_kirim || null,
                    tanggal: o.tanggal || o.created_at,
                    created_at: o.created_at,
                  }));
                  mapped.forEach(pushATK);
                  try { localStorage.setItem('atk_orders_history_karyawan', JSON.stringify(mapped)); } catch { }
                  atkLoaded = true;
                }
              }
            } catch { }
          }
          if (!atkLoaded) {
            const cached = JSON.parse(localStorage.getItem('atk_orders_history_karyawan') || localStorage.getItem('atk_orders_history') || '[]');
            cached.forEach(pushATK);
          }
        } catch (e) { console.warn('ATK karyawan riwayat error:', e); }
      }

      // 2. IT Tickets
      try {
        if (userId) {
          let tickets = [];
          let allTickets = [];

          try {
            const [userRes, allRes] = await Promise.all([
              fetch(`https://jne-cilacap.com/slip/public/api/request-it?user_created=${userId}`).catch(() => null),
              fetch(`https://jne-cilacap.com/slip/public/api/request-it`).catch(() => null),
            ]);

            if (userRes && userRes.ok) {
              const userJson = await userRes.json();
              tickets = userJson.data || (Array.isArray(userJson) ? userJson : []);
            }

            if (allRes && allRes.ok) {
              const allJson = await allRes.json();
              allTickets = allJson.data || (Array.isArray(allJson) ? allJson : []);
            }
          } catch (e) {
            console.warn('Gagal fetch IT tickets:', e);
          }

          if (tickets.length === 0 && allTickets.length > 0 && userId) {
            tickets = allTickets.filter(t => String(t?.user_created) === String(userId));
          }
          if (allTickets.length === 0 && tickets.length > 0) {
            allTickets = tickets;
          }

          const getItSI = (ticket) => {
            const status = String(ticket?.status || 'order').toLowerCase().trim();
            if (['received', 'selesai', 'approved'].includes(status))
              return { label: 'Selesai', type: 'approved', progress: 100, approver: `Oleh ${ticket?.oleh || 'Tim IT'}`, initial: 'IT', color: '#10b981' };
            if (['sedang', 'proses', 'diproses'].includes(status))
              return { label: 'Diproses', type: 'process', progress: 70, approver: ticket?.oleh ? `Oleh ${ticket.oleh}` : 'Ditangani Tim IT', initial: 'IT', color: '#3b82f6' };
            if (['rejected', 'ditolak', 'batal', 'lewati'].includes(status))
              return { label: 'Ditolak', type: 'rejected', progress: 100, approver: 'Ditolak IT', initial: '✕', color: '#ef4444' };
            return { label: 'Ngantri', type: 'pending', progress: 35, approver: 'Menunggu Tim IT', initial: 'IT', color: '#f59e0b' };
          };

          const isPendingTicket = (t) => {
            const s = String(t?.status || 'order').toLowerCase().trim();
            const isActive = t?.active === 1 || t?.active === '1' || t?.active === true || t?.active == null;
            return isActive && !['received', 'selesai', 'approved', 'sedang', 'proses', 'diproses', 'rejected', 'ditolak', 'batal', 'lewati'].some(k => s.includes(k));
          };

          const isProcessTicket = (t) => {
            const s = String(t?.status || '').toLowerCase().trim();
            const isActive = t?.active === 1 || t?.active === '1' || t?.active === true || t?.active == null;
            return isActive && ['sedang', 'proses', 'diproses'].some(k => s.includes(k));
          };

          const pendingTickets = allTickets
            .filter(isPendingTicket)
            .slice()
            .sort((a, b) => {
              const timeA = new Date(a.created_at || a.tanggal || 0).getTime();
              const timeB = new Date(b.created_at || b.tanggal || 0).getTime();
              if (timeA !== timeB) return timeA - timeB;
              return (Number(a.id) || 0) - (Number(b.id) || 0);
            });

          const pendingQueueMap = {};
          pendingTickets.forEach((t, qi) => { pendingQueueMap[t.id] = qi + 1; });

          const processTickets = allTickets
            .filter(isProcessTicket)
            .slice()
            .sort((a, b) => {
              const timeA = new Date(a.created_at || a.tanggal || 0).getTime();
              const timeB = new Date(b.created_at || b.tanggal || 0).getTime();
              if (timeA !== timeB) return timeA - timeB;
              return (Number(a.id) || 0) - (Number(b.id) || 0);
            });
          const processQueueMap = {};
          processTickets.forEach((t, qi) => { processQueueMap[t.id] = qi + 1; });

          tickets.forEach((ticket, idx) => {
            const si = getItSI(ticket);
            const tanggal = ticket?.tanggal || ticket?.created_at;
            const mainTitle = ticket?.perangkat
              ? `${ticket.perangkat}: ${ticket.problem || 'Permintaan IT'}`
              : ticket?.no_tiket || `Tiket IT #${ticket?.id || idx + 1}`;

            let subtitle;
            if (si.type === 'pending') {
              const qPos = pendingQueueMap[ticket?.id];
              const total = pendingTickets.length;
              if (qPos != null) {
                const aheadCount = qPos - 1;
                subtitle = aheadCount > 0
                  ? `Menunggu ${aheadCount} antrian (urutan ke-${qPos} dari ${total} antrian)`
                  : `Giliran berikutnya! (urutan ke-${qPos} dari ${total} antrian)`;
              } else {
                subtitle = 'Menunggu Tim IT';
              }
            } else if (si.type === 'process') {
              const qPos = processQueueMap[ticket?.id];
              const totalProcess = processTickets.length;
              if (qPos != null) {
                subtitle = totalProcess > 1
                  ? `Sedang diproses (antrian ke-${qPos} dari ${totalProcess})`
                  : `Sedang diproses (antrian ke-1)`;
              } else {
                subtitle = 'Sedang diproses oleh Tim IT';
              }
            } else {
              subtitle = [ticket?.dari, ticket?.problem || 'Keluhan / Permintaan Bantuan IT'].filter(Boolean).join(' — ');
            }

            result.push({
              id: `kit-${ticket?.id || idx}`,
              category: 'it',
              categoryLabel: 'IT',
              title: mainTitle,
              subtitle,
              date: formatTanggal(tanggal),
              rawDate: new Date(tanggal || Date.now()).getTime(),
              statusText: si.label,
              statusType: si.type,
              progress: si.progress,
              approverLabel: si.approver,
              approverInitial: si.initial,
              approverColor: si.color,
              route: '/request-it',
            });
          });
        }
      } catch (e) { console.warn('IT karyawan riwayat error:', e); }

      // 3. Asset Pengajuan (dari IndexedDB)
      try {
        const { getAllPengajuan } = await import('../db/assetDB');
        const pengajuan = await getAllPengajuan();

        const getAssetStatusInfo = (rawStatus) => {
          const s = String(rawStatus || 'Pending').toLowerCase().trim();
          if (['selesai', 'approved', 'done'].includes(s)) {
            return { label: 'Selesai', type: 'approved', progress: 100, approver: 'Oleh Tim Asset', initial: 'AS', color: '#059669' };
          }
          if (['diproses', 'proses'].includes(s)) {
            return { label: 'Diproses', type: 'process', progress: 70, approver: 'Diproses Tim Asset', initial: 'AS', color: '#2563eb' };
          }
          if (['ditolak', 'rejected'].includes(s)) {
            return { label: 'Ditolak', type: 'rejected', progress: 100, approver: 'Ditolak Tim Asset', initial: '✕', color: '#dc2626' };
          }
          return { label: 'Menunggu', type: 'pending', progress: 35, approver: 'Menunggu Tim Asset', initial: 'AS', color: '#d97706' };
        };

        pengajuan.forEach((p, idx) => {
          const st = getAssetStatusInfo(p.status);
          const jenisRaw = String(p.jenis || '').toLowerCase();
          const isPengadaan = jenisRaw === 'pengadaan' || String(p.tipe || '').toLowerCase().includes('pengadaan') || !p.kode_asset;
          const assetTitle = isPengadaan
            ? `Pengadaan: ${p.nama_asset || 'Asset Baru'}`
            : `Service: ${p.nama_asset || p.kode_asset || (p.penanganan_label || p.jenis || 'Asset')}`;
          const assetSubtitle = `${p.kode_asset ? `[${p.kode_asset}] ` : ''}${p.deskripsi || p.penanganan_label || p.keterangan || (isPengadaan ? 'Pengajuan pengadaan unit baru' : 'Perbaikan / perawatan asset')}`;

          result.push({
            id: `kasset-${p.id || idx}`,
            category: 'asset',
            categoryLabel: isPengadaan ? 'Pengadaan' : 'Service',
            title: assetTitle,
            subtitle: assetSubtitle,
            date: formatTanggal(p.created_at),
            rawDate: new Date(p.created_at || Date.now()).getTime(),
            statusText: st.label,
            statusType: st.type,
            progress: st.progress,
            approverLabel: st.approver,
            approverInitial: st.initial,
            approverColor: st.color,
            route: '/lapor',
          });
        });
      } catch (e) {
        console.warn('Asset karyawan riwayat error:', e);
      }

      result.sort((a, b) => b.rawDate - a.rawDate);
      if (!cancelled) {
        setKaryawanAtkItItems(result);
        setKaryawanAtkItLoading(false);
      }
    };

    loadKaryawanAtkIt();
    return () => { cancelled = true; };
  }, [isAgen, trigger]);

  // Fetch semua riwayat ATK + IT + Asset untuk agen
  useEffect(() => {
    if (!isAgen) return;
    let cancelled = false;

    const loadAgenItems = async () => {
      setAgenLoading(true);
      const userId = getUserId();
      const result = [];

      // ── 1. ATK: dari API (dengan fallback localStorage) ──
      try {
        const getAtkStatusInfo = (rawStatus, petugas, agenNameParam) => {
          const s = String(rawStatus || 'baru').toLowerCase().trim();
          const approverName = petugas ? `Oleh ${petugas}` : 'Oleh GA/HC';
          const approverInitial = petugas
            ? petugas.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
            : 'GA';

          // diterima / erima / terima → Sudah Diterima (status akhir: 'oleh' diganti nama agennya)
          if (['diterima', 'terima', 'erima', 'sudah diterima'].some(k => s.includes(k))) {
            const rawAgen = agenNameParam || getAgenName() || 'Agen';
            const cleanAgen = String(rawAgen).replace(/^oleh\s+/i, '').trim();
            const agenInitial = cleanAgen
              .split(/\s+/)
              .filter(Boolean)
              .map(w => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase() || 'AG';

            return {
              label: 'Sudah Diterima',
              type: 'approved',
              progress: 100,
              approver: `Oleh ${cleanAgen}`,
              initial: agenInitial,
              color: '#0891b2',
            };
          }
          // dikirim / kirim → Sudah Dikirim
          if (['dikirim', 'kirim', 'sudah dikirim'].some(k => s.includes(k))) {
            return { label: 'Sudah Dikirim', type: 'approved', progress: 90, approver: approverName || 'Sedang Dikirim GA', initial: approverInitial, color: '#7c3aed' };
          }
          // diisiapkan / disiapkan / approved / selesai / siap → Sudah Disiapkan
          if (['diisiapkan', 'disiapkan', 'approved', 'selesai', 'siap', 'sudah disiapkan'].some(k => s.includes(k)) || s.includes('siap')) {
            return { label: 'Sudah Disiapkan', type: 'approved', progress: 75, approver: approverName, initial: approverInitial, color: '#059669' };
          }
          // proses / diproses → Sedang Disiapkan
          if (['proses', 'diproses', 'sedang disiapkan'].some(k => s.includes(k))) {
            return { label: 'Sedang Disiapkan', type: 'process', progress: 50, approver: approverName || 'Sedang Disiapkan GA', initial: approverInitial, color: '#2563eb' };
          }
          // rejected / ditolak
          if (['rejected', 'ditolak', 'batal'].some(k => s.includes(k))) {
            return { label: 'Ditolak', type: 'rejected', progress: 100, approver: approverName || 'Ditolak GA', initial: '✕', color: '#dc2626' };
          }
          // default: baru / pending / order → Ngantri / Belum Disiapkan
          return { label: 'Ngantri / Belum Disiapkan', type: 'pending', progress: 20, approver: 'Menunggu Disiapkan', initial: 'GA', color: '#d97706' };
        };

        const pushATKOrder = (o, idx) => {
          const st = getAtkStatusInfo(o.status, o.petugas, o.agen || o.pemohon || o.connote_agen);
          const itemNames = Array.isArray(o.items) && o.items.length > 0
            ? o.items.map(i => i.nama + (i.qty ? ` (${i.qty})` : '')).join(', ')
            : (o.keperluan || 'Kebutuhan ATK Operasional');
          result.push({
            id: `atk-${o.id || o.no_order || idx}`,
            category: 'atk',
            categoryLabel: 'ATK',
            title: o.no_order || `Order ATK #${o.id || idx + 1}`,
            subtitle: itemNames,
            date: formatTanggal(o.tanggal || o.created_at),
            rawDate: new Date(o.tanggal || o.created_at || Date.now()).getTime(),
            statusText: st.label,
            statusType: st.type,
            progress: st.progress,
            approverLabel: st.approver,
            approverInitial: st.initial,
            approverColor: st.color,
            route: '/atk-pemesanan',
            rawOrder: o,
          });
        };

        let atkLoaded = false;
        if (userId && !isFemaleUser) {
          try {
            const atkRes = await fetch(`https://jne-cilacap.com/slip/public/api/atk/orders?user_created=${userId}`);
            if (atkRes.ok) {
              const atkJson = await atkRes.json();
              if (atkJson.status === 'success' && Array.isArray(atkJson.data) && atkJson.data.length > 0) {
                const mapped = atkJson.data.map((o) => ({
                  id: o.id,
                  raw_id: o.id,
                  no_order: o.no_nota || `ATK-${o.id}`,
                  items: (o.details || []).map(d => ({
                    id: d.id_barang,
                    nama: d.barang?.nama || d.nama_barang || 'Barang',
                    qty: d.jumlah,
                    satuan: d.barang?.satuan || 'Pcs',
                    kode: d.barang?.kode || '-',
                  })),
                  keperluan: o.keterangan || '-',
                  instruksi: o.keterangan || '-',
                  pemohon: o.agen || 'AGEN RINJANI',
                  lokasi: o.shift || 'JNE RINJANI',
                  status: o.status || 'baru',
                  petugas: o.petugas || null,
                  agen: o.agen || o.connote_agen || null,
                  catatan: o.catatan || null,
                  catatan_kirim: o.catatan_kirim || null,
                  tanggal: o.tanggal || o.created_at,
                  created_at: o.created_at,
                }));
                mapped.forEach(pushATKOrder);
                try { localStorage.setItem('atk_orders_history', JSON.stringify(mapped)); } catch { }
                atkLoaded = true;
              }
            }
          } catch { }
        }
        // Fallback ke localStorage jika belum ter-load dari API
        if (!atkLoaded) {
          const atkOrders = JSON.parse(localStorage.getItem('atk_orders_history') || '[]');
          atkOrders.forEach(pushATKOrder);
        }
      } catch (e) { console.warn('ATK riwayat error:', e); }

      // ── 2. IT Tickets: dari API ──
      try {
        if (userId) {
          const res = await fetch(
            `https://jne-cilacap.com/slip/public/api/request-it?user_created=${userId}`
          );

          const json = await res.json();
          const tickets = json.data || (Array.isArray(json) ? json : []);

          console.log('IT Tickets:', tickets);

          // Helper status tiket IT
          const getItStatusInfo = (ticket) => {
            const status = String(ticket?.status || 'order')
              .toLowerCase()
              .trim();

            if (['received', 'selesai', 'approved'].includes(status)) {
              return {
                label: 'Selesai',
                type: 'approved',
                progress: 100,
                approver: `Oleh ${ticket?.oleh || 'Tim IT'}`,
                initial: 'IT',
                color: '#10b981',
              };
            }

            if (['sedang', 'proses', 'diproses'].includes(status)) {
              return {
                label: 'Diproses',
                type: 'process',
                progress: 70,
                approver: ticket?.oleh ? `Oleh ${ticket.oleh}` : 'Ditangani Tim IT',
                initial: 'IT',
                color: '#3b82f6',
              };
            }

            if (['rejected', 'ditolak', 'batal', 'lewati'].includes(status)) {
              return {
                label: 'Ditolak',
                type: 'rejected',
                progress: 100,
                approver: 'Ditolak IT',
                initial: '✕',
                color: '#ef4444',
              };
            }

            // Default: order / pending
            return {
              label: 'Ngantri',
              type: 'pending',
              progress: 35,
              approver: 'Menunggu Tim IT',
              initial: 'IT',
              color: '#f59e0b',
            };
          };

          // 1. Hitung antrian untuk tiket yang sedang diproses (sedang, proses, diproses)
          const processTickets = tickets
            .filter(t => {
              const s = String(t?.status || '').toLowerCase().trim();
              return ['sedang', 'proses', 'diproses'].some(k => s.includes(k));
            })
            .slice()
            .sort((a, b) => new Date(a.created_at || a.tanggal || 0) - new Date(b.created_at || b.tanggal || 0));

          const processQueueMap = {};
          processTickets.forEach((t, qi) => {
            processQueueMap[t.id] = qi + 1;
          });

          // 2. Hitung posisi antrian: tiket berstatus pending (Ngantri), sort ascending by created_at
          const pendingTickets = tickets
            .filter(t => {
              const s = String(t?.status || 'order').toLowerCase().trim();
              return !['received', 'selesai', 'approved', 'sedang', 'proses', 'diproses', 'rejected', 'ditolak', 'batal', 'lewati'].some(k => s.includes(k));
            })
            .slice()
            .sort((a, b) => new Date(a.created_at || a.tanggal || 0) - new Date(b.created_at || b.tanggal || 0));

          const pendingQueueMap = {};
          pendingTickets.forEach((t, qi) => {
            pendingQueueMap[t.id] = qi + 1;
          });

          tickets.forEach((ticket, idx) => {
            const statusInfo = getItStatusInfo(ticket);

            const tanggal = ticket?.tanggal || ticket?.created_at;

            const mainTitle = ticket?.perangkat
              ? `${ticket.perangkat}: ${ticket.problem || 'Permintaan IT'}`
              : ticket?.no_tiket || `Tiket IT #${ticket?.id || idx + 1}`;

            // Tampilkan posisi antrian untuk status Ngantri maupun Diproses
            let subtitle;
            if (statusInfo.type === 'pending') {
              const qPos = pendingQueueMap[ticket?.id];
              const totalQueue = pendingTickets.length;
              if (qPos != null) {
                const aheadCount = qPos - 1;
                subtitle = aheadCount > 0
                  ? `Menunggu ${aheadCount} antrian (urutan ke-${qPos} dari ${totalQueue})`
                  : `Giliran berikutnya! (urutan ke-${qPos} dari ${totalQueue})`;
              } else {
                subtitle = 'Menunggu Tim IT';
              }
            } else if (statusInfo.type === 'process') {
              const qPos = processQueueMap[ticket?.id];
              const totalProcess = processTickets.length;
              if (qPos != null) {
                subtitle = totalProcess > 1
                  ? `Sedang diproses (antrian ke-${qPos} dari ${totalProcess})`
                  : `Sedang diproses (antrian ke-1)`;
              } else {
                subtitle = 'Sedang diproses oleh Tim IT';
              }
            } else {
              subtitle = [
                ticket?.dari,
                ticket?.problem || 'Keluhan / Permintaan Bantuan IT',
              ]
                .filter(Boolean)
                .join(' — ');
            }

            result.push({
              id: `it-${ticket?.id || idx}`,
              category: 'it',
              categoryLabel: 'IT',

              title: mainTitle,
              subtitle,

              date: formatTanggal(tanggal),
              rawDate: new Date(tanggal || Date.now()).getTime(),

              statusText: statusInfo.label,
              statusType: statusInfo.type,
              progress: statusInfo.progress,

              approverLabel: statusInfo.approver,
              approverInitial: statusInfo.initial,
              approverColor: statusInfo.color,

              route: '/request-it',
            });
          });
        }
      } catch (e) {
        console.warn('IT riwayat error:', e);
      }


      // ── 3. Asset Pengajuan: dari IndexedDB ──
      try {
        const { getAllPengajuan } = await import('../db/assetDB');
        const pengajuan = await getAllPengajuan();

        const getAssetStatusInfo = (rawStatus) => {
          const s = String(rawStatus || 'Pending').toLowerCase().trim();
          if (['selesai', 'approved', 'done'].includes(s)) {
            return { label: 'Selesai', type: 'approved', progress: 100, approver: 'Oleh Tim Asset', initial: 'AS', color: '#059669' };
          }
          if (['diproses', 'proses'].includes(s)) {
            return { label: 'Diproses', type: 'process', progress: 70, approver: 'Diproses Tim Asset', initial: 'AS', color: '#2563eb' };
          }
          if (['ditolak', 'rejected'].includes(s)) {
            return { label: 'Ditolak', type: 'rejected', progress: 100, approver: 'Ditolak Tim Asset', initial: '✕', color: '#dc2626' };
          }
          // default: Pending / ngantri
          return { label: 'Menunggu', type: 'pending', progress: 35, approver: 'Menunggu Tim Asset', initial: 'AS', color: '#d97706' };
        };

        pengajuan.forEach((p, idx) => {
          const st = getAssetStatusInfo(p.status);
          const jenisRaw = String(p.jenis || '').toLowerCase();
          const isPengadaan = jenisRaw === 'pengadaan' || String(p.tipe || '').toLowerCase().includes('pengadaan') || !p.kode_asset;
          const assetTitle = isPengadaan
            ? `Pengadaan: ${p.nama_asset || 'Asset Baru'}`
            : `Service: ${p.nama_asset || p.kode_asset || (p.penanganan_label || p.jenis || 'Asset')}`;
          const assetSubtitle = `${p.kode_asset ? `[${p.kode_asset}] ` : ''}${p.deskripsi || p.penanganan_label || p.keterangan || (isPengadaan ? 'Pengajuan pengadaan unit baru' : 'Perbaikan / perawatan asset')}`;

          result.push({
            id: `asset-${p.id || idx}`,
            category: 'asset',
            categoryLabel: isPengadaan ? 'Pengadaan' : 'Service',
            title: assetTitle,
            subtitle: assetSubtitle,
            date: formatTanggal(p.created_at),
            rawDate: new Date(p.created_at || Date.now()).getTime(),
            statusText: st.label,
            statusType: st.type,
            progress: st.progress,
            approverLabel: st.approver,
            approverInitial: st.initial,
            approverColor: st.color,
            route: '/lapor',
          });
        });
      } catch (e) { console.warn('Asset riwayat error:', e); }

      // Sort by tanggal terbaru
      result.sort((a, b) => b.rawDate - a.rawDate);

      if (!cancelled) {
        setAgenItems(result);
        setAgenLoading(false);
      }
    };

    loadAgenItems();
    return () => { cancelled = true; };
  }, [isAgen, trigger]);

  // ── AGEN VIEW: Riwayat pengajuan ATK / IT / Asset dengan Tampilan Dua Nada (Two-Tone Card) ──
  if (isAgen) {
    const countAtk = agenItems.filter(i => i.category === 'atk').length;
    const countIt = agenItems.filter(i => i.category === 'it').length;
    const countAsset = agenItems.filter(i => i.category === 'asset').length;

    const filteredAgenItems = agenFilter === 'all' ? agenItems : agenItems.filter(i => i.category === agenFilter);
    const displayed = filteredAgenItems.slice(0, 4);

    const chipsMeta = [
      {
        key: 'all',
        label: 'Semua',
        count: agenItems.length,
        selBg: '#2563eb', selBorder: '#2563eb', selShadow: 'rgba(37, 99, 235, 0.25)',
        unBg: '#f8fafc', unBorder: '#e2e8f0', unText: '#475569', unBadgeBg: '#e2e8f0', unBadgeText: '#475569'
      },
      {
        key: 'atk',
        label: '📦 ATK',
        count: countAtk,
        selBg: isFemaleUser ? '#94a3b8' : '#7c3aed',
        selBorder: isFemaleUser ? '#94a3b8' : '#7c3aed',
        selShadow: isFemaleUser ? 'rgba(148, 163, 184, 0.25)' : 'rgba(124, 58, 237, 0.25)',
        unBg: isFemaleUser ? '#f1f5f9' : '#ede9fe',
        unBorder: isFemaleUser ? '#e2e8f0' : '#ddd6fe',
        unText: isFemaleUser ? '#64748b' : '#6d28d9',
        unBadgeBg: 'rgba(255, 255, 255, 0.7)',
        unBadgeText: isFemaleUser ? '#94a3b8' : '#7c3aed'
      },
      {
        key: 'it',
        label: '💻 IT',
        count: countIt,
        selBg: '#0284c7', selBorder: '#0284c7', selShadow: 'rgba(2, 132, 199, 0.25)',
        unBg: '#e0f2fe', unBorder: '#bae6fd', unText: '#0369a1', unBadgeBg: 'rgba(255, 255, 255, 0.7)', unBadgeText: '#0284c7'
      },
      {
        key: 'asset',
        label: '🗂️ Asset',
        count: countAsset,
        selBg: '#0d9488', selBorder: '#0d9488', selShadow: 'rgba(13, 148, 136, 0.25)',
        unBg: '#ccfbf1', unBorder: '#99f6e4', unText: '#0f766e', unBadgeBg: 'rgba(255, 255, 255, 0.7)', unBadgeText: '#0d9488'
      },
    ];

    const handleAgenViewAll = () => {
      if (agenFilter === 'atk') {
        if (isFemaleUser) {
          Swal.fire({ icon: 'warning', title: 'Akses Dibatasi', text: 'Menu pemesanan ATK tidak tersedia untuk akun Anda.', confirmButtonText: 'Mengerti' });
        } else {
          navigate('/atk-pemesanan');
        }
      }
      else if (agenFilter === 'it') navigate('/request-it');
      else if (agenFilter === 'asset') navigate('/lapor', { state: { activeTab: 'asset' } });
      else {
        if (!isFemaleUser) navigate('/atk-pemesanan');
        else navigate('/request-it');
      }
    };

    return (
      <div style={{
        maxWidth: 450,
        margin: '0 auto 20px',
        padding: '0 4px',
        boxSizing: 'border-box',
        width: '100%',
      }}>
        {/* ── Header ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          padding: '0 4px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#2563eb',
              boxShadow: '0 0 8px rgba(37, 99, 235, 0.6)'
            }} />
            <h3 style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: '#1e293b',
              letterSpacing: '-0.3px',
            }}>
              Riwayat Pengajuan
            </h3>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={() => { setAgenLoading(true); setTrigger(t => t + 1); }}
              title="Refresh Riwayat"
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                padding: '4px 6px',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                transition: 'opacity 0.2s',
              }}
            >
              <RefreshCw size={14} className={agenLoading ? 'animate-spin' : ''} />
            </button>
            <button
              type="button"
              onClick={handleAgenViewAll}
              style={{
                background: 'none',
                border: 'none',
                color: '#059669',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                padding: '4px 6px',
                borderRadius: 8,
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Lihat Semua
              <ChevronRight size={15} />
            </button>
          </div>
        </div>

        {/* ── Filter Chips ── */}
        <div style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          paddingBottom: 10,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          {chipsMeta.map((chip) => {
            const isSelected = agenFilter === chip.key;
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => setAgenFilter(chip.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 600,
                  border: isSelected ? `1px solid ${chip.selBorder}` : `1px solid ${chip.unBorder}`,
                  backgroundColor: isSelected ? chip.selBg : chip.unBg,
                  color: isSelected ? '#ffffff' : chip.unText,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease',
                  boxShadow: isSelected ? `0 2px 8px ${chip.selShadow}` : 'none',
                }}
              >
                <span>{chip.label}</span>
                {chip.count > 0 && (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.25)' : chip.unBadgeBg,
                    color: isSelected ? '#ffffff' : chip.unBadgeText,
                    padding: '1px 6px',
                    borderRadius: 10,
                  }}>
                    {chip.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Card List Dua Nada (Two-Tone Layout Sesuai Desain Riwayat Sebelumnya) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <AnimatePresence mode="popLayout">
            {agenLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}
              >
                Memuat riwayat pengajuan...
              </motion.div>
            ) : displayed.length > 0 ? (
              displayed.map((item, idx) => {
                // Warna soft body sesuai kategori
                const typeColorMap = {
                  atk: { bodyBg: '#ede9fe', headerBg: '#ffffff', headerText: '#6b7280' },    // soft purple body
                  it: { bodyBg: '#e0f2fe', headerBg: '#ffffff', headerText: '#6b7280' },     // soft blue body
                  asset: { bodyBg: '#ccfbf1', headerBg: '#ffffff', headerText: '#6b7280' },  // soft teal body
                };
                const typeColors = typeColorMap[item.category] || { bodyBg: '#f1f5f9', headerBg: '#ffffff', headerText: '#6b7280' };
                const bodyBg = typeColors.bodyBg;
                const lightHeaderBg = typeColors.headerBg;
                const lightHeaderTextColor = typeColors.headerText;

                // Status badge colors
                const statusColors = item.statusType === 'approved'
                  ? { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46', icon: '#10b981' }
                  : item.statusType === 'rejected'
                    ? { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', icon: '#ef4444' }
                    : item.statusType === 'process'
                      ? { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af', icon: '#2563eb' }
                      : { bg: '#fef9c3', border: '#fde68a', text: '#854d0e', icon: '#d97706' };

                return (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.25, delay: idx * 0.05 }}
                    onClick={() => {
                      if (item.category === 'asset') {
                        navigate('/lapor', { state: { activeTab: 'asset' } });
                      } else if (item.category === 'atk' && item.rawOrder) {
                        setSelectedAtkOrder(item.rawOrder);
                      } else {
                        navigate(item.route);
                      }
                    }}
                    style={{
                      borderRadius: 24,
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.06)',
                      border: '1px solid rgba(0,0,0,0.08)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    whileTap={{ scale: 0.98 }}
                    whileHover={{ y: -2 }}
                  >
                    {/* ── Two-Tone Top Bar: Left Tab + S-Curve Connector + Right Section ── */}
                    <div style={{
                      display: 'flex',
                      width: '100%',
                      height: 44,
                      alignItems: 'stretch',
                    }}>
                      {/* Left Tab with bodyBg */}
                      <div style={{
                        backgroundColor: bodyBg,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        paddingLeft: 14,
                        paddingRight: 2,
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: '4px 10px',
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          backgroundColor: statusColors.bg,
                          border: `1px solid ${statusColors.border}`,
                          color: statusColors.text,
                        }}>
                          {item.statusType === 'approved' ? (
                            <CheckCircle2 size={12} color={statusColors.icon} />
                          ) : item.statusType === 'rejected' ? (
                            <AlertCircle size={12} color={statusColors.icon} />
                          ) : item.statusType === 'process' ? (
                            <RefreshCw size={12} color={statusColors.icon} />
                          ) : (
                            <Clock size={12} color={statusColors.icon} />
                          )}
                          <span>{item.statusText}</span>
                        </div>

                        {/* Mini Trend / Accent Circle */}
                        <div style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#64748b',
                        }}>
                          <TrendingUp size={11} />
                        </div>
                      </div>

                      {/* S-Curve SVG Connector */}
                      <div style={{ width: 34, height: 44, flexShrink: 0 }}>
                        <svg width="34" height="44" viewBox="0 0 34 44" fill="none" style={{ display: 'block' }}>
                          <rect width="34" height="44" fill={bodyBg} />
                          <path d="M 0 0 C 17 0, 17 44, 34 44 L 34 0 Z" fill={lightHeaderBg} />
                        </svg>
                      </div>

                      {/* Right Section ("PIC / Pengurus") with lightHeaderBg */}
                      <div style={{
                        flex: 1,
                        minWidth: 0,
                        backgroundColor: lightHeaderBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: 14,
                        gap: 7,
                        overflow: 'hidden',
                      }}>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: lightHeaderTextColor,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                          flex: 1,
                          textAlign: 'right',
                        }}>
                          {item.approverLabel}
                        </span>
                        <div style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          backgroundColor: item.approverColor || '#2563eb',
                          color: '#ffffff',
                          fontSize: 10,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                          flexShrink: 0,
                        }}>
                          {item.approverInitial}
                        </div>
                      </div>
                    </div>

                    {/* ── Main Content: Category Icon + Title + Progress + Subtitle ── */}
                    <div style={{
                      backgroundColor: bodyBg,
                      padding: '10px 16px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}>
                      {/* Circle Icon Badge */}
                      {renderCategoryIcon(item.category, null, false)}

                      {/* Title, Subtitle, Progress Bar */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          minWidth: 0,
                          overflow: 'hidden',
                        }}>
                          <h4 style={{
                            margin: 0,
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: '#0f172a',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            letterSpacing: '-0.2px',
                            minWidth: 0,
                            flex: 1,
                          }}>
                            {item.title}
                          </h4>

                          <div style={{
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}>
                            <MoreHorizontal size={16} />
                          </div>
                        </div>

                        {/* Progress Line Track */}
                        <div style={{
                          width: '100%',
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          marginTop: 7,
                          marginBottom: 6,
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${item.progress || 35}%`,
                            height: '100%',
                            borderRadius: 2,
                            backgroundColor: statusColors.icon,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>

                        {/* Subtitle / Details */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontSize: 11,
                          color: '#64748b',
                          gap: 6,
                        }}>
                          <span style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1,
                          }}>
                            {item.subtitle}
                          </span>
                          {item.date && (
                            <span style={{
                              flexShrink: 0,
                              fontWeight: 500,
                              color: '#475569',
                            }}>
                              {item.date}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              /* ── Empty State ── */
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: 24,
                  padding: '28px 18px',
                  textAlign: 'center',
                  border: '1px dashed #cbd5e1',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                }}
              >
                <div style={{
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  backgroundColor: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px',
                  color: '#94a3b8',
                  fontSize: 22,
                }}>
                  {agenFilter === 'atk' ? <Package size={22} /> : agenFilter === 'it' ? <Laptop size={22} /> : agenFilter === 'asset' ? <Wrench size={22} /> : <FileText size={22} />}
                </div>
                <p style={{ margin: 0, fontWeight: 700, color: '#334155', fontSize: 13 }}>
                  Belum ada riwayat pengajuan {agenFilter !== 'all' ? agenFilter.toUpperCase() : ''}
                </p>
                <p style={{ margin: '4px 0 14px', color: '#94a3b8', fontSize: 11.5 }}>
                  Pengajuan ATK, tiket IT, atau pengajuan Asset akan muncul di sini beserta statusnya.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (agenFilter === 'it') navigate('/request-it');
                    else if (agenFilter === 'asset') navigate('/lapor', { state: { activeTab: 'asset' } });
                    else if (agenFilter === 'atk' && isFemaleUser) {
                      Swal.fire({ icon: 'warning', title: 'Akses Dibatasi', text: 'Menu pemesanan ATK tidak tersedia untuk akun Anda.', confirmButtonText: 'Mengerti' });
                    } else {
                      navigate('/atk-pemesanan');
                    }
                  }}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 20,
                    backgroundColor: agenFilter === 'it' ? '#0284c7' : agenFilter === 'asset' ? '#0d9488' : '#7c3aed',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  {agenFilter === 'it' ? 'Buat Tiket IT' : agenFilter === 'asset' ? 'Buat Pengajuan Asset' : 'Buat Pengajuan ATK'}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Button Lihat Semua jika data lebih dari yang ditampilkan */}
        {filteredAgenItems.length > 4 && (
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button
              type="button"
              onClick={handleAgenViewAll}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                padding: '6px 12px',
                borderRadius: 16,
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Lihat {filteredAgenItems.length - 4} pengajuan lainnya →
            </button>
          </div>
        )}

        {/* ── MODAL DETAIL PESANAN ATK (HOME AGEN) ── */}
        <AnimatePresence>
          {selectedAtkOrder && (
            <motion.div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => e.target === e.currentTarget && setSelectedAtkOrder(null)}
            >
              <motion.div
                style={{
                  width: '100%',
                  maxWidth: 480,
                  maxHeight: '90vh',
                  backgroundColor: '#ffffff',
                  borderRadius: '24px 24px 0 0',
                  padding: '20px 20px 32px',
                  overflowY: 'auto',
                  boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.15)',
                  boxSizing: 'border-box',
                }}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Detail Pesanan</h2>
                  <button
                    type="button"
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedAtkOrder(null)}
                    aria-label="Tutup detail"
                  >
                    <X size={18} color="#374151" />
                  </button>
                </div>

                {/* Status Badge */}
                {(() => {
                  const sLower = String(selectedAtkOrder.status || '').toLowerCase().trim();
                  const isTerima = sLower.includes('erima') || sLower.includes('terima');
                  const isKirim = sLower.includes('kirim');
                  const isSiap = sLower.includes('siap') || ['approved', 'selesai'].includes(sLower);

                  const badgeCfg = isTerima
                    ? { label: 'Sudah Diterima', color: '#0891b2', bg: '#ecfeff' }
                    : isKirim
                      ? { label: 'Sudah Dikirim', color: '#7c3aed', bg: '#f5f3ff' }
                      : isSiap
                        ? { label: 'Sudah Disiapkan', color: '#059669', bg: '#ecfdf5' }
                        : { label: 'Ngantri / Belum Disiapkan', color: '#d97706', bg: '#fffbeb' };

                  return (
                    <div style={{ marginBottom: 16 }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '5px 14px',
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          backgroundColor: badgeCfg.bg,
                          color: badgeCfg.color,
                        }}
                      >
                        <CheckCircle size={14} /> {badgeCfg.label}
                      </span>
                    </div>
                  );
                })()}

                {/* Detail Rows */}
                {(() => {
                  const sLower = String(selectedAtkOrder.status || '').toLowerCase().trim();
                  const isSiap = sLower.includes('siap') || ['approved', 'selesai'].includes(sLower);
                  const isKirim = sLower.includes('kirim');
                  const isTerima = sLower.includes('erima') || sLower.includes('terima');

                  const rows = [
                    ['No. Order', selectedAtkOrder.no_order],
                    ['Tanggal', formatTanggal(selectedAtkOrder.tanggal || selectedAtkOrder.created_at)],
                    ['Pemohon', selectedAtkOrder.pemohon || selectedAtkOrder.agen || getAgenName()],
                    ['Lokasi', selectedAtkOrder.lokasi || 'JNE RINJANI'],
                    ['Instruksi', selectedAtkOrder.instruksi || selectedAtkOrder.keperluan || '-'],
                  ];

                  if ((isSiap || isKirim || isTerima) && selectedAtkOrder.catatan) {
                    rows.push(['Disiapkan', selectedAtkOrder.catatan]);
                  }

                  if ((isKirim || isTerima) && selectedAtkOrder.catatan_kirim) {
                    rows.push(['Catatan Kirim', selectedAtkOrder.catatan_kirim]);
                  }

                  return rows.filter(([, v]) => v).map(([k, v]) => (
                    <div
                      key={k}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        padding: '10px 0',
                        borderBottom: '1px solid #f3f4f6',
                        gap: 12,
                      }}
                    >
                      <span style={{ color: '#6b7280', fontSize: 13, flexShrink: 0 }}>{k}</span>
                      <span style={{ color: '#111827', fontSize: 13, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
                    </div>
                  ));
                })()}

                {/* Items */}
                <p style={{ color: '#6b7280', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '16px 0 8px', margin: 0 }}>
                  ITEM PESANAN ({(selectedAtkOrder.items || []).length})
                </p>
                {(selectedAtkOrder.items || []).map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 12px',
                      borderRadius: 12,
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: 24, lineHeight: 1 }}>📦</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827' }}>{item.nama}</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                        {item.qty} {item.satuan || 'Pasang'} · {item.kode || 'C08'}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Action: Konfirmasi Diterima jika status dikirim */}
                {String(selectedAtkOrder.status || '').toLowerCase().includes('kirim') && (
                  <button
                    type="button"
                    onClick={async () => {
                      const orderId = selectedAtkOrder.raw_id || selectedAtkOrder.id;
                      const orderNo = selectedAtkOrder.no_order || selectedAtkOrder.id;

                      const confirm = await Swal.fire({
                        title: 'Konfirmasi Penerimaan?',
                        html: `Apakah pesanan ATK <b>${orderNo}</b> sudah Anda terima dengan baik dan lengkap?`,
                        icon: 'question',
                        showCancelButton: true,
                        confirmButtonColor: '#059669',
                        cancelButtonColor: '#6b7280',
                        confirmButtonText: 'Ya, Sudah Diterima',
                        cancelButtonText: 'Batal',
                      });

                      if (!confirm.isConfirmed) return;

                      Swal.fire({
                        title: 'Memproses...',
                        text: 'Memperbarui status pesanan menjadi Sudah Diterima...',
                        allowOutsideClick: false,
                        didOpen: () => { Swal.showLoading(); },
                      });

                      try {
                        const endpoints = [
                          `https://jne-cilacap.com/slip/public/api/atk/order/terima/${orderId}`,
                          `https://jne-cilacap.com/slip/public/api/atk/order/status/${orderId}`,
                        ];

                        for (const ep of endpoints) {
                          try {
                            const res = await fetch(ep, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                              body: JSON.stringify({
                                id: orderId,
                                status: 'diterima',
                                user_id: userId,
                                catatan_terima: `Diterima oleh ${getAgenName()}`,
                              }),
                            });
                            if (res.ok) break;
                          } catch { }
                        }

                        const updatedStatus = 'diterima';
                        setSelectedAtkOrder((prev) => prev ? { ...prev, status: updatedStatus } : null);

                        try {
                          const cached = JSON.parse(localStorage.getItem('atk_orders_history') || '[]');
                          const updatedCache = cached.map((o) =>
                            (o.id === selectedAtkOrder.id || o.no_order === selectedAtkOrder.no_order) ? { ...o, status: updatedStatus } : o
                          );
                          localStorage.setItem('atk_orders_history', JSON.stringify(updatedCache));
                        } catch { }

                        loadAgenItems();

                        await Swal.fire({
                          icon: 'success',
                          title: 'Pesanan Diterima!',
                          text: `Pesanan ${orderNo} berhasil dikonfirmasi telah diterima.`,
                          timer: 2000,
                          showConfirmButton: false,
                        });
                      } catch (err) {
                        setSelectedAtkOrder((prev) => prev ? { ...prev, status: 'diterima' } : null);
                        loadAgenItems();
                        Swal.fire({
                          icon: 'success',
                          title: 'Pesanan Diterima',
                          text: 'Status telah diperbarui di perangkat Anda.',
                        });
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '13px',
                      borderRadius: 14,
                      border: 'none',
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: 'pointer',
                      marginTop: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
                    }}
                  >
                    <CheckCircle size={18} /> Konfirmasi Diterima
                  </button>
                )}

                {/* Badge info jika sudah diterima */}
                {(String(selectedAtkOrder.status || '').toLowerCase().includes('erima') || String(selectedAtkOrder.status || '').toLowerCase().includes('terima')) && (
                  <div
                    style={{
                      marginTop: 20,
                      padding: '12px 16px',
                      borderRadius: 12,
                      backgroundColor: '#ecfeff',
                      border: '1px solid #a5f3fc',
                      color: '#0e7490',
                      fontSize: 13,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    <CheckCircle size={16} color="#0891b2" /> Pesanan telah dikonfirmasi diterima
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 450,
      margin: '0 auto 20px',
      padding: '0 4px',
      boxSizing: 'border-box',
      width: '100%',
    }}>
      {/* ── Section Header ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
        padding: '0 4px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#2563eb',
            boxShadow: '0 0 8px rgba(37, 99, 235, 0.6)'
          }} />
          <h3 style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 700,
            color: '#1e293b',
            letterSpacing: '-0.3px',
          }}>
            Riwayat Pengajuan
          </h3>
        </div>

        <button
          type="button"
          onClick={handleViewAll}
          style={{
            background: 'none',
            border: 'none',
            color: '#059669',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: '4px 6px',
            borderRadius: 8,
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          Lihat Semua
          <ChevronRight size={15} />
        </button>
      </div>

      {/* ── Filter Chips ── */}
      <div style={{
        display: 'flex',
        gap: 8,
        overflowX: 'auto',
        paddingBottom: 10,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
        <style>{`div::-webkit-scrollbar { display: none; }`}</style>
        {(() => {
          const chipColorMap = {
            all: {
              unselectedBg: '#f8fafc', unselectedBorder: '#e2e8f0', unselectedText: '#475569',
              unselectedBadgeBg: '#e2e8f0', unselectedBadgeText: '#475569',
              selectedBg: '#2563eb', selectedBorder: '#2563eb', selectedShadow: 'rgba(37, 99, 235, 0.25)',
            },
            absen: {
              unselectedBg: '#e0f2fe', unselectedBorder: '#bae6fd', unselectedText: '#0369a1',
              unselectedBadgeBg: 'rgba(255, 255, 255, 0.7)', unselectedBadgeText: '#0284c7',
              selectedBg: '#0284c7', selectedBorder: '#0284c7', selectedShadow: 'rgba(2, 132, 199, 0.25)',
            },
            izin: {
              unselectedBg: '#fff7ed', unselectedBorder: '#fed7aa', unselectedText: '#c2410c',
              unselectedBadgeBg: 'rgba(255, 255, 255, 0.7)', unselectedBadgeText: '#ea580c',
              selectedBg: '#ea580c', selectedBorder: '#ea580c', selectedShadow: 'rgba(234, 88, 12, 0.25)',
            },
            uang: {
              unselectedBg: '#ede9fe', unselectedBorder: '#ddd6fe', unselectedText: '#6d28d9',
              unselectedBadgeBg: 'rgba(255, 255, 255, 0.7)', unselectedBadgeText: '#7c3aed',
              selectedBg: '#7c3aed', selectedBorder: '#7c3aed', selectedShadow: 'rgba(124, 58, 237, 0.25)',
            },
            atk: {
              unselectedBg: '#f5f3ff', unselectedBorder: '#ddd6fe', unselectedText: '#6d28d9',
              unselectedBadgeBg: 'rgba(255, 255, 255, 0.7)', unselectedBadgeText: '#7c3aed',
              selectedBg: '#7c3aed', selectedBorder: '#7c3aed', selectedShadow: 'rgba(124, 58, 237, 0.25)',
            },
            it: {
              unselectedBg: '#e0f2fe', unselectedBorder: '#bae6fd', unselectedText: '#0369a1',
              unselectedBadgeBg: 'rgba(255, 255, 255, 0.7)', unselectedBadgeText: '#0284c7',
              selectedBg: '#0284c7', selectedBorder: '#0284c7', selectedShadow: 'rgba(2, 132, 199, 0.25)',
            },
            asset: {
              unselectedBg: '#ccfbf1', unselectedBorder: '#99f6e4', unselectedText: '#0f766e',
              unselectedBadgeBg: 'rgba(255, 255, 255, 0.7)', unselectedBadgeText: '#0d9488',
              selectedBg: '#0d9488', selectedBorder: '#0d9488', selectedShadow: 'rgba(13, 148, 136, 0.25)',
            },
          };

          const tabs = [
            { key: 'all', label: '🗂️ Semua', count: combinedItems.length },
            { key: 'absen', label: '🕐 Absen', count: countAbsen },
            { key: 'izin', label: '📅 Izin', count: countIzin },
            { key: 'uang', label: '💰 Uang', count: countUang },
            { key: 'asset', label: '🔧 Asset', count: countAsset },
            ...(!isFemaleUserShared ? [{ key: 'atk', label: '📦 ATK', count: countAtk }] : []),
            { key: 'it', label: '💻 IT', count: countIt },
          ];

          return tabs.map((tab) => {
            const isSelected = activeFilter === tab.key;
            const theme = chipColorMap[tab.key] || chipColorMap.all;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveFilter(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  border: isSelected ? `1px solid ${theme.selectedBorder}` : `1px solid ${theme.unselectedBorder}`,
                  backgroundColor: isSelected ? theme.selectedBg : theme.unselectedBg,
                  color: isSelected ? '#ffffff' : theme.unselectedText,
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s ease',
                  boxShadow: isSelected ? `0 2px 8px ${theme.selectedShadow}` : 'none',
                }}
              >
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.25)' : theme.unselectedBadgeBg,
                    color: isSelected ? '#ffffff' : theme.unselectedBadgeText,
                    padding: '1px 6px', borderRadius: 10,
                  }}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          });
        })()}
      </div>

      {/* ── Card List (Desain Modern Sesuai Gambar Referensi) ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AnimatePresence mode="popLayout">
          {(karyawanAtkItLoading && (activeFilter === 'atk' || activeFilter === 'it' || activeFilter === 'all')) ? (
            <motion.div key="loading-atk-it" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>
              Memuat riwayat ATK & IT...
            </motion.div>
          ) : displayedItems.length > 0 ? (
            displayedItems.map((item, idx) => {
              // Warna berdasarkan kategori (soft pastel)
              const typeColorMap = {
                absen: { bodyBg: '#e0f2fe', headerBg: '#ffffff' },
                izin: { bodyBg: '#fff7ed', headerBg: '#ffffff' },
                uang: { bodyBg: '#ede9fe', headerBg: '#ffffff' },
                atk: { bodyBg: '#f5f3ff', headerBg: '#ffffff' },
                it: { bodyBg: '#e0f2fe', headerBg: '#ffffff' },
              };
              const typeColors = typeColorMap[item.category?.toLowerCase()] || { bodyBg: '#f1f5f9', headerBg: '#ffffff' };
              const bodyBg = typeColors.bodyBg;
              const lightHeaderBg = typeColors.headerBg;
              const lightHeaderTextColor = '#475569';

              // Status badge colors
              const statusColors = item.statusType === 'approved'
                ? { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46', icon: '#10b981' }
                : item.statusType === 'rejected'
                  ? { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', icon: '#ef4444' }
                  : item.statusType === 'process'
                    ? { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af', icon: '#2563eb' }
                    : { bg: '#fef9c3', border: '#fde68a', text: '#854d0e', icon: '#f59e0b' };

              return (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.25, delay: idx * 0.05 }}
                  onClick={() => handleCardClick(item)}
                  style={{
                    borderRadius: 24,
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  whileTap={{ scale: 0.98 }}
                  whileHover={{ y: -2 }}
                >
                  {/* ── Two-Tone Top Bar: Left Tab + S-Curve Connector + Right Section (Agak Terang) ── */}
                  <div style={{
                    display: 'flex',
                    width: '100%',
                    height: 44,
                    alignItems: 'stretch',
                  }}>
                    {/* Left Tab with bodyBg */}
                    <div style={{
                      backgroundColor: bodyBg,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      paddingLeft: 14,
                      paddingRight: 2,
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '4px 10px',
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: statusColors.bg,
                        border: `1px solid ${statusColors.border}`,
                        color: statusColors.text,
                      }}>
                        {item.statusType === 'approved' ? (
                          <CheckCircle2 size={12} color={statusColors.icon} />
                        ) : item.statusType === 'rejected' ? (
                          <AlertCircle size={12} color={statusColors.icon} />
                        ) : item.statusType === 'process' ? (
                          <RefreshCw size={12} color={statusColors.icon} />
                        ) : (
                          <Clock size={12} color={statusColors.icon} />
                        )}
                        <span>{item.statusText}</span>
                      </div>

                      {/* Mini Trend / Accent Circle */}
                      <div style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b',
                      }}>
                        <TrendingUp size={11} />
                      </div>
                    </div>

                    {/* S-Curve SVG Connector between left tab and right light section */}
                    <div style={{ width: 34, height: 44, flexShrink: 0 }}>
                      <svg width="34" height="44" viewBox="0 0 34 44" fill="none" style={{ display: 'block' }}>
                        <rect width="34" height="44" fill={bodyBg} />
                        <path d="M 0 0 C 17 0, 17 44, 34 44 L 34 0 Z" fill={lightHeaderBg} />
                      </svg>
                    </div>

                    {/* Right Section ("Oleh Saya") with lightHeaderBg (AGAK TERANG) */}
                    <div style={{
                      flex: 1,
                      minWidth: 0,
                      backgroundColor: lightHeaderBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: 14,
                      gap: 7,
                      overflow: 'hidden',
                    }}>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: lightHeaderTextColor,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                        flex: 1,
                        textAlign: 'right',
                      }}>
                        {item.approverLabel || (item.authorName === 'Saya' ? 'Oleh Saya' : `Dari ${item.authorName}`)}
                      </span>
                      <div style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: item.approverColor || '#2563eb',
                        color: '#ffffff',
                        fontSize: 10.5,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                        flexShrink: 0,
                      }}>
                        {item.approverInitial || (item.authorName || 'U')[0].toUpperCase()}
                      </div>
                    </div>
                  </div>

                  {/* ── Main Content: Icon + Title + Progress + More (with bodyBg) ── */}
                  <div style={{
                    backgroundColor: bodyBg,
                    padding: '10px 16px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}>
                    {/* Circle Icon Badge */}
                    {renderCategoryIcon(item.category, item.iconType, false)}

                    {/* Title, Subtitle, Progress Bar */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        minWidth: 0,
                        overflow: 'hidden',
                      }}>
                        <h4 style={{
                          margin: 0,
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: '#0f172a',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          letterSpacing: '-0.2px',
                          minWidth: 0,
                          flex: 1,
                        }}>
                          {item.title}
                        </h4>

                        <div style={{
                          color: '#94a3b8',
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}>
                          <MoreHorizontal size={16} />
                        </div>
                      </div>

                      {/* Progress Line Track */}
                      <div style={{
                        width: '100%',
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        marginTop: 7,
                        marginBottom: 6,
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${item.progress}%`,
                          height: '100%',
                          borderRadius: 2,
                          backgroundColor: statusColors.icon,
                          transition: 'width 0.4s ease',
                        }} />
                      </div>

                      {/* Subtitle / Details */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: 11,
                        color: '#64748b',
                        gap: 6,
                      }}>
                        <span style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                        }}>
                          {item.subtitle}
                        </span>
                        {item.date && (
                          <span style={{
                            flexShrink: 0,
                            fontWeight: 500,
                            color: '#475569',
                          }}>
                            {item.date}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })
          ) : (
            /* ── Empty State ── */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: 20,
                padding: '24px 18px',
                textAlign: 'center',
                border: '1px dashed #cbd5e1',
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center',
                justifyContent: 'center', margin: '0 auto 10px', color: '#94a3b8',
              }}>
                {activeFilter === 'atk' ? <Package size={22} /> : activeFilter === 'it' ? <Laptop size={22} /> : activeFilter === 'asset' ? <Wrench size={22} /> : activeFilter === 'absen' ? <Clock size={22} /> : activeFilter === 'izin' ? <Calendar size={22} /> : activeFilter === 'uang' ? <Wallet size={22} /> : <FileText size={22} />}
              </div>
              <p style={{ margin: 0, fontWeight: 700, color: '#334155', fontSize: 13 }}>
                Belum ada riwayat {activeFilter === 'atk' ? 'ATK' : activeFilter === 'it' ? 'Request IT' : activeFilter === 'asset' ? 'Asset' : activeFilter === 'absen' ? 'Absen' : activeFilter === 'izin' ? 'Izin' : activeFilter === 'uang' ? 'Uang' : 'pengajuan'}
              </p>
              <p style={{ margin: '4px 0 12px', color: '#94a3b8', fontSize: 11.5 }}>
                {activeFilter === 'atk' ? 'Pengajuan ATK akan muncul di sini.' : activeFilter === 'it' ? 'Tiket IT Anda akan muncul di sini.' : activeFilter === 'asset' ? 'Pengajuan pengadaan / service asset akan muncul di sini.' : 'Pengajuan absen lapangan, izin, kasbon, ATK, atau tiket IT akan muncul di sini.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  if (activeFilter === 'atk') navigate('/atk-pemesanan');
                  else if (activeFilter === 'it') navigate('/request-it');
                  else if (activeFilter === 'asset') navigate('/lapor', { state: { activeTab: 'asset' } });
                  else navigate('/lapor');
                }}
                style={{
                  padding: '7px 16px', borderRadius: 20,
                  backgroundColor: activeFilter === 'atk' ? '#7c3aed' : activeFilter === 'it' ? '#0284c7' : activeFilter === 'asset' ? '#0d9488' : '#2563eb',
                  color: '#ffffff', border: 'none', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                }}
              >
                {activeFilter === 'atk' ? 'Pesan ATK' : activeFilter === 'it' ? 'Buat Tiket IT' : activeFilter === 'asset' ? 'Buka Menu Asset' : 'Buka Menu Notif'}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Button Lihat Semua jika data lebih dari yang ditampilkan */}
      {filteredList.length > 4 && (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button
            type="button"
            onClick={handleViewAll}
            style={{
              background: 'none', border: 'none', color: '#2563eb',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              padding: '6px 12px', borderRadius: 16, transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            Lihat {filteredList.length - 4} {activeFilter === 'atk' ? 'order ATK' : activeFilter === 'it' ? 'tiket IT' : 'laporan'} lainnya →
          </button>
        </div>
      )}

      {/* ── MODAL DETAIL PESANAN ATK (HOME KARYAWAN) ── */}
      <AnimatePresence>
        {selectedAtkOrderKaryawan && (
          <motion.div
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.45)',
              zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setSelectedAtkOrderKaryawan(null)}
          >
            <motion.div
              style={{
                width: '100%', maxWidth: 480, maxHeight: '90vh',
                backgroundColor: '#ffffff', borderRadius: '24px 24px 0 0',
                padding: '20px 20px 32px', overflowY: 'auto',
                boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.15)', boxSizing: 'border-box',
              }}
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Detail Pesanan ATK</h2>
                <button
                  type="button"
                  style={{
                    width: 34, height: 34, borderRadius: '50%', backgroundColor: '#f3f4f6',
                    border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                  onClick={() => setSelectedAtkOrderKaryawan(null)}
                  aria-label="Tutup detail"
                >
                  <X size={18} color="#374151" />
                </button>
              </div>

              {/* Status Badge */}
              {(() => {
                const sLower = String(selectedAtkOrderKaryawan.status || '').toLowerCase().trim();
                const isTerima = sLower.includes('erima') || sLower.includes('terima');
                const isKirim = sLower.includes('kirim');
                const isSiap = sLower.includes('siap') || ['approved', 'selesai'].includes(sLower);
                const isProses = ['proses', 'diproses', 'sedang'].some(k => sLower.includes(k));
                const badgeCfg = isTerima
                  ? { label: 'Sudah Diterima', color: '#0891b2', bg: '#ecfeff' }
                  : isKirim ? { label: 'Sudah Dikirim', color: '#7c3aed', bg: '#f5f3ff' }
                  : isSiap ? { label: 'Sudah Disiapkan', color: '#059669', bg: '#ecfdf5' }
                  : isProses ? { label: 'Sedang Disiapkan', color: '#2563eb', bg: '#eff6ff' }
                  : { label: 'Ngantri / Belum Disiapkan', color: '#d97706', bg: '#fffbeb' };
                return (
                  <div style={{ marginBottom: 16 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700,
                      backgroundColor: badgeCfg.bg, color: badgeCfg.color,
                    }}>
                      <CheckCircle size={14} /> {badgeCfg.label}
                    </span>
                  </div>
                );
              })()}

              {/* Detail Rows */}
              {(() => {
                const sLower = String(selectedAtkOrderKaryawan.status || '').toLowerCase().trim();
                const isSiap = sLower.includes('siap') || ['approved', 'selesai'].includes(sLower);
                const isKirim = sLower.includes('kirim');
                const isTerima = sLower.includes('erima') || sLower.includes('terima');
                const rows = [
                  ['No. Order', selectedAtkOrderKaryawan.no_order],
                  ['Tanggal', formatTanggal(selectedAtkOrderKaryawan.tanggal || selectedAtkOrderKaryawan.created_at)],
                  ['Pemohon', selectedAtkOrderKaryawan.pemohon || selectedAtkOrderKaryawan.agen || getAgenNameShared()],
                  ['Lokasi', selectedAtkOrderKaryawan.lokasi || '-'],
                  ['Instruksi', selectedAtkOrderKaryawan.instruksi || selectedAtkOrderKaryawan.keperluan || '-'],
                ];
                if ((isSiap || isKirim || isTerima) && selectedAtkOrderKaryawan.catatan) rows.push(['Disiapkan', selectedAtkOrderKaryawan.catatan]);
                if ((isKirim || isTerima) && selectedAtkOrderKaryawan.catatan_kirim) rows.push(['Catatan Kirim', selectedAtkOrderKaryawan.catatan_kirim]);
                return rows.filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #f3f4f6', gap: 12 }}>
                    <span style={{ color: '#6b7280', fontSize: 13, flexShrink: 0 }}>{k}</span>
                    <span style={{ color: '#111827', fontSize: 13, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
                  </div>
                ));
              })()}

              {/* Items */}
              <p style={{ color: '#6b7280', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '16px 0 8px', margin: 0 }}>
                ITEM PESANAN ({(selectedAtkOrderKaryawan.items || []).length})
              </p>
              {(selectedAtkOrderKaryawan.items || []).map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 12, backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb', marginBottom: 8,
                }}>
                  <span style={{ fontSize: 24, lineHeight: 1 }}>📦</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827' }}>{item.nama}</p>
                    <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>{item.qty} {item.satuan || 'Pcs'} · {item.kode || '-'}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RiwayatNotifikasiCard;
